import { GameSummaryDrawDays } from '@/components/GameSummaryDrawDays';
import SummaryAdditionalGame from '@/components/SummaryAdditionalGame';
import { SummaryCoupons } from '@/components/SummaryCoupons';
import { WeeksNumberSelection } from '@/components/WeeksNumberSelection';
import { CouponGroupConfig, DrawTypeConfig, GameConfig } from '@/config/game';
import { createPlural } from '@/helpers/createPlural';
import { CouponGroupState } from '@/reducers/couponGroup';
import numberToCurrency from '@/utilities/numberFormatter';
import React from 'react';

export interface GameSummaryProps {
  selectedNumbers: CouponGroupState;
  selectedPlayNumber: number[];
  selectedAdditionalGameIds?: number[];
  selectedWeeksNumber: number;
  selectedWeeklySubscription: boolean;
  selectedDrawDays: string[];
  selectedDrawNumber: number;
  selectedDrawRate: number;
  superNumberIndex: number;
  gameShortName: string;
  hasDrawNumbers?: boolean;
  hasDrawRate?: boolean;
  drawTypes?: DrawTypeConfig[];
  weeksNumber: GameConfig['weeksNumber'];
  onWeeksNumberChange: (value: number) => void;
  hasWeeksNumberSelection?: boolean;
  noHidePlayNumber?: boolean;
  additionalGames?: GameConfig[];
  couponGroupsConfig?: CouponGroupConfig;
  currentGame?: GameConfig;
}

export const GameSummary: React.FC<
  React.PropsWithChildren<GameSummaryProps>
> = ({
  selectedNumbers,
  selectedPlayNumber,
  selectedAdditionalGameIds,
  selectedWeeksNumber,
  selectedWeeklySubscription,
  selectedDrawDays,
  selectedDrawNumber,
  selectedDrawRate,
  superNumberIndex,
  gameShortName,
  hasDrawNumbers,
  hasDrawRate,
  drawTypes,
  weeksNumber,
  onWeeksNumberChange,
  hasWeeksNumberSelection,
  noHidePlayNumber,
  additionalGames,
  couponGroupsConfig,
  currentGame,
}) => {
  const maxAmountCoupons = currentGame?.couponGroup?.amountCoupons || 0;
  const selectedNumbersIds = Object.keys(selectedNumbers);
  const couponsAmount = selectedNumbersIds.length;
  const filledCoupons = selectedNumbersIds.reduce(
    (prev, current) => (selectedNumbers[current][0].length > 0 ? ++prev : prev),
    0
  );
  const numbersByDigitAmount = (additionalGame) =>
    selectedPlayNumber.slice(
      selectedPlayNumber.length - additionalGame.digitAmount
    );

  const selectedAdditionalGames =
    selectedAdditionalGameIds &&
    additionalGames &&
    additionalGames.filter((game) =>
      selectedAdditionalGameIds.some(
        (gameId) => gameId === game.additionalGameId
      )
    );

  const validCoupons =
    filledCoupons > maxAmountCoupons ? maxAmountCoupons : filledCoupons;
  const headlineDetails = `(${validCoupons} von ${maxAmountCoupons} Feldern)`;

  return (
    <div className="GameSummary">
      {!!couponsAmount && (
        <div className="GameSummary__container">
          <h3 className="GameSummary__title">
            <span>Ihre Zahlen </span>
            <span className="h-font-light">{headlineDetails}</span>
          </h3>
          <SummaryCoupons
            selectedNumbers={selectedNumbers}
            selectedPlayNumber={selectedPlayNumber}
            superNumberIndex={superNumberIndex}
            gameShortName={gameShortName}
            drawTypes={drawTypes}
            couponGroupsConfig={couponGroupsConfig}
            validCoupons={validCoupons}
          />
        </div>
      )}

      {selectedAdditionalGames &&
        (noHidePlayNumber ||
          (!!selectedPlayNumber.length &&
            selectedAdditionalGames &&
            !!selectedAdditionalGames.length)) && (
          <div className="GameSummary__container">
            <div className="h-flex-gm h-flex-gm--cols">
              <div className="h-flex__col h-spacer-large-bottom h-spacer-no-bottom--gm">
                <h4 className="GameSummary__title">Losnummer</h4>
                <span className="GameSummary__playNumber">
                  {selectedPlayNumber.join('')}
                </span>
              </div>

              <div className="h-flex__col">
                {selectedAdditionalGames.length > 0 && (
                  <>
                    <h4 className="GameSummary__title">Zusatzlotterien</h4>
                    {selectedAdditionalGames.map(
                      (currentGame) =>
                        currentGame && (
                          <SummaryAdditionalGame
                            key={currentGame.id}
                            game={currentGame}
                            numbers={numbersByDigitAmount(currentGame)}
                          />
                        )
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

      {!hasDrawNumbers && (
        <div className="GameSummary__container">
          <div className="h-flex-gm h-flex-gm--cols">
            <div className="h-flex__col h-spacer-no-bottom--gm h-spacer-large-bottom">
              <h4 className="GameSummary__title">Anzahl der Wochen</h4>

              {hasWeeksNumberSelection && weeksNumber ? (
                <WeeksNumberSelection
                  options={weeksNumber}
                  label="Gewählte Spieldauer in Wochen"
                  selectedValue={selectedWeeksNumber}
                  onChange={onWeeksNumberChange}
                />
              ) : (
                <p className="GameSummary__weeks">
                  {selectedWeeklySubscription
                    ? 'Dauerspiel'
                    : `${selectedWeeksNumber} Woche${
                        selectedWeeksNumber > 1 ? 'n' : ''
                      }`}
                </p>
              )}
            </div>

            {selectedDrawDays.length > 0 && currentGame && (
              <div className="h-flex__col">
                <h4 className="GameSummary__title">
                  Teilnahme an den Ziehungen
                </h4>

                <GameSummaryDrawDays
                  games={[currentGame, ...(selectedAdditionalGames || [])]}
                  selectedWeeksNumber={selectedWeeksNumber}
                  selectedDrawDays={selectedDrawDays}
                  selectedWeeklySubscription={selectedWeeklySubscription}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {(hasDrawNumbers || hasDrawRate) && (
        <div className="GameSummary__container">
          <div className="h-flex-gm h-flex-gm--cols">
            {selectedDrawRate && (
              <div className="h-flex__col">
                <h4 className="GameSummary__title">Einsatz pro Spielfeld</h4>
                <p>{numberToCurrency(selectedDrawRate)}</p>
              </div>
            )}

            {selectedDrawNumber && (
              <div className="h-flex__col h-spacer-large-bottom h-spacer-no-bottom--gm">
                <h4 className="GameSummary__title">Anzahl der Ziehungen</h4>
                <p className="GameSummary__weeks">
                  {`${selectedDrawNumber} ${createPlural(+selectedDrawNumber, [
                    'Ziehung',
                    'Ziehungen',
                  ])}`}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
